import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * Single source of truth for auth-gated routes. Wrap any page that requires
 * a signed-in user (optionally with a specific role) so unauthenticated
 * visitors are consistently redirected to /login?next=<original-path> and
 * mismatched-role users land on a friendly not-authorised page.
 *
 * Usage:
 *   <RequireAuth><CustomerDashboard /></RequireAuth>
 *   <RequireAuth roles={["super_admin"]}><AdminDashboard /></RequireAuth>
 *   <RequireAuth roles={["business", "super_admin"]}><BusinessDashboard /></RequireAuth>
 */
export default function RequireAuth({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();

  // While the initial /auth/me probe is in flight, render nothing. Prevents
  // gated pages from flashing their "please sign in" state and prevents pages
  // like AdminCases from firing authenticated fetches before we know who the
  // viewer is.
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--gf-bg)", color: "var(--gf-text-muted)" }}
        data-testid="require-auth-loading"
      >
        <div className="text-sm">Loading…</div>
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(loc.pathname + (loc.search || ""));
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (roles && roles.length > 0 && !roles.includes(user.role)) {
    // Feb 27 2026 — Privacy hardening. Never surface internal role names
    // ("super_admin", "business", etc.) to signed-in users on the 403 page.
    // The actual role guard above still enforces access — this is a
    // wording-only change so we don't leak our internal role vocabulary.
    return (
      <div className="min-h-screen" style={{ background: "var(--gf-bg)" }} data-testid="require-auth-forbidden">
        <div className="max-w-md mx-auto px-6 py-24 text-center">
          <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>
            403 · Access restricted
          </div>
          <h1 className="font-display text-3xl mt-3" style={{ color: "var(--gf-text)" }}>
            You don&rsquo;t have permission to view this page
          </h1>
          <p className="mt-3 text-sm" style={{ color: "var(--gf-text-muted)" }}>
            If you believe this is a mistake, please contact support.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <a
              href="/"
              className="px-5 py-2 rounded-full font-medium"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
              data-testid="require-auth-back-home"
            >
              Back to home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
